import React from "react";
import { cn } from "@/lib/utils";
import Eyebrow from "./Eyebrow";
import Reveal from "./Reveal";

/**
 * PageHero — consistent hero for non-landing public pages.
 * Provides eyebrow + title + lede + CTA row + optional decorative orbs.
 *
 *   <PageHero
 *     eyebrow="About"
 *     title={<>Building <span className="gradient-text">AI that ships</span></>}
 *     description="..."
 *     actions={<><Button>...</Button><Button>...</Button></>}
 *   />
 */
const PageHero = ({
  eyebrow,
  eyebrowIcon,
  title,
  description,
  actions,
  align = "center",
  className = "",
  children,
}) => {
  const isCenter = align === "center";
  return (
    <section
      className={cn(
        "relative page-top pb-12 md:pb-16 overflow-hidden aurora",
        className,
      )}
    >
      <div className="grid-bg absolute inset-0 pointer-events-none opacity-50" aria-hidden="true" />
      <div className="container-page relative z-10">
        <div
          className={cn(
            "max-w-4xl",
            isCenter ? "mx-auto text-center" : "text-left",
          )}
        >
          {eyebrow && (
            <Reveal variant="up" duration={0.5}>
              <div className={cn("mb-5", isCenter ? "flex justify-center" : "")}>
                <Eyebrow icon={eyebrowIcon}>{eyebrow}</Eyebrow>
              </div>
            </Reveal>
          )}

          {title && (
            <Reveal variant="up" delay={0.05} duration={0.8}>
              <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
                {title}
              </h1>
            </Reveal>
          )}

          {description && (
            <Reveal variant="up" delay={0.12} duration={0.8}>
              <p
                className={cn(
                  "mt-6 text-base sm:text-lg lg:text-xl text-[var(--text-secondary)] leading-relaxed max-w-3xl",
                  isCenter ? "mx-auto" : "",
                )}
              >
                {description}
              </p>
            </Reveal>
          )}

          {actions && (
            <Reveal variant="up" delay={0.18} duration={0.8}>
              <div
                className={cn(
                  "mt-8 flex flex-col sm:flex-row gap-3",
                  isCenter ? "items-center justify-center" : "items-start",
                )}
              >
                {actions}
              </div>
            </Reveal>
          )}

          {children}
        </div>
      </div>
    </section>
  );
};

export default PageHero;
